/**
 * Pool module data hooks. Every query hits a route gated by the server-side POOL ROLE — completely
 * separate from the RBAC roles/permissions the rest of the portal uses — so these hooks are NOT gated
 * on `hasPermission()` the way useWorklists.ts gates on `worklist:read`. A caller without enough pool
 * role simply gets a 403 back, which the screen renders via <PoolErrorState/> instead of treating as a
 * crash. See api/types.ts's Pool module banner for the full rationale.
 *
 * Two exceptions to that rule, both below: `usePoolAccess` never 403s (it exists precisely so the FE
 * can ask "what's my pool role?" without triggering one), and `usePoolSystemState`/
 * `useSetPoolSystemState` are gated `_manage` (pool role owner or admin), not `_view` — see types.ts's
 * PoolAccess banner.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { api, ApiRequestError } from "@/api/client";
import type { AccountLookup } from "@/features/pool/poolAuditFormat";
import { startConvergeLoop } from "@/features/pool/convergeLifecycle";
import type {
  PoolAddCredentialBody,
  PoolAddOperatorBody,
  PoolBulkForceAction,
  PoolCloseBody,
  PoolCreatePeriodBody,
  PoolForceActionResult,
  PoolRenewBody,
  PoolRenewDecisionBody,
  PoolResolveIntentBody,
  PoolSetSystemStateBody,
  PoolUpdatePeriodBody,
  PoolUser,
  PoolUsers,
} from "@/api/types";

/** Debounce a value by `delay` ms — mirrors features/search/useSearch.ts's useDebounced. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const DEBOUNCE_MS = 250;
/** Below this length the candidates search doesn't fire — a blank query would return every account. */
export const MIN_CANDIDATE_CHARS = 2;

export const poolKeys = {
  candidates: (q: string) => ["pool", "candidates", q] as const,
  userCandidates: (q: string) => ["pool", "userCandidates", q] as const,
  accounts: ["pool", "accounts"] as const,
  users: (q: string, accountId?: string, license?: "2" | "1") =>
    ["pool", "users", q, accountId ?? null, license ?? null] as const,
  history: (email: string) => ["pool", "history", email] as const,
  operators: ["pool", "operators"] as const,
  dashboard: ["pool", "dashboard"] as const,
  status: ["pool", "status"] as const,
  audit: (filters: PoolAuditFilters) => ["pool", "audit", filters] as const,
  reportsDaily: (range: { start: string; end: string }) =>
    ["pool", "reportsDaily", range.start, range.end] as const,
  reportDay: (date: string) => ["pool", "reportDay", date] as const,
  intents: ["pool", "intents"] as const,
  access: ["pool", "access"] as const,
  systemState: ["pool", "systemState"] as const,
};

/** Search accounts eligible to become a pool. Debounced; below MIN_CANDIDATE_CHARS the query is
 *  disabled (an empty/1-char term would return most of the estate). */
export function usePoolCandidates(term: string) {
  const trimmed = term.trim();
  const debounced = useDebounced(trimmed, DEBOUNCE_MS).trim();
  const active = debounced.length >= MIN_CANDIDATE_CHARS;
  return useQuery({
    queryKey: poolKeys.candidates(debounced),
    queryFn: ({ signal }) => api.poolCandidates(debounced, signal),
    enabled: active,
  });
}

/** Search portal users to back the Settings "grant access" picker (rev 6 R3) — same debounce/threshold
 *  shape as usePoolCandidates above, over a different endpoint. */
export function usePoolUserCandidates(term: string) {
  const trimmed = term.trim();
  const debounced = useDebounced(trimmed, DEBOUNCE_MS).trim();
  const active = debounced.length >= MIN_CANDIDATE_CHARS;
  return useQuery({
    queryKey: poolKeys.userCandidates(debounced),
    queryFn: ({ signal }) => api.poolUserCandidates(debounced, signal),
    enabled: active,
  });
}

export function usePoolAccounts() {
  return useQuery({
    queryKey: poolKeys.accounts,
    queryFn: ({ signal }) => api.poolAccounts(signal),
  });
}

/**
 * account_id → `{name, number}` for the CURRENT pool estate, memoized off usePoolAccounts — the fallback
 * source poolAuditFormat's account_id humanizer reads when an audit `details` change doesn't carry the
 * BE-shipped name itself (r10 22/7: raw Zoom ids in the audit Detail column, prod screenshot feedback). A
 * REMOVED account won't be in this map — the shipped-name path is what covers that case; this is only
 * ever the fallback. Pending/errored accounts query → an empty map, so callers still fall through to the
 * raw id rather than throwing.
 */
export function usePoolAccountLookup(): AccountLookup {
  const accountsQ = usePoolAccounts();
  const accounts = accountsQ.data?.accounts;
  return useMemo(() => {
    const byId = new Map<string, { name: string | null; number: string | null }>();
    for (const a of accounts ?? []) {
      byId.set(a.account_id, { name: a.account_name, number: a.account_number });
    }
    return (accountId: string) => byId.get(accountId);
  }, [accounts]);
}

/** The UI-level License filter (PoolUsersPage, rev 9; "needs-review" added r12 22/7) — "all" and
 *  "needs-review" both mean no server-side filter at all (neither is ever put on the wire); only "2"/"1"
 *  (Zoom's own Licensed/Basic user_type) are real query values. "needs-review" fetches the exact same
 *  unfiltered rows as "all" (see licenseWireValue below — they share one cache entry) and narrows them
 *  CLIENT-SIDE via `poolUserNeedsReview`, because the condition it's after (an open period paired with a
 *  NON-Licensed Zoom membership) isn't a `user_type` value the server can filter on by itself. */
export type PoolUserLicenseFilter = "2" | "1" | "all" | "needs-review";

function licenseWireValue(license?: PoolUserLicenseFilter): "2" | "1" | undefined {
  return license === "2" || license === "1" ? license : undefined;
}

// วันนี้แบบ "ปฏิทินไทย" เสมอ (r12 lens LOW): browser-local จะเพี้ยนหนึ่งวันช่วงรอยต่อเที่ยงคืน
// ถ้า operator เปิดจากโซนเวลาอื่น — BE เทียบด้วยวัน BKK ฝั่งนี้ต้องตรงกัน
const todayIso = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Bangkok" }).format(new Date());

/**
 * "Paid but Basic" (r12 22/7, principal feedback, prod screenshots) — an OPEN period (period_id set,
 * not yet expired) whose customer's current Zoom membership isn't Licensed (Basic, OR no membership at
 * all — dashboard card 2's own "we have it, Zoom doesn't" condition, see PoolDashboardPage.tsx). ONE
 * definition shared by the Pool Users "needs-review" filter AND the paid-but-Basic badge next to the
 * License cell, so the two can never quietly drift apart. `period_end >= today` (string compare — both
 * are already "YYYY-MM-DD", which sorts identically to a real date compare) excludes a period that has
 * simply lapsed; that's the sweep's job to clean up, not an operator's "check this now" flag.
 */
export function poolUserNeedsReview(user: PoolUser): boolean {
  return (
    user.period_id != null &&
    user.period_end != null &&
    user.period_end >= todayIso() &&
    user.user_type !== 2
  );
}

/**
 * rev 17b (Manage-every-row matrix, rev17b-addendum.md) — a row whose Manage button should open the
 * new `UnlinkOnlyDialog` instead of `PeriodModal`: not a CURRENT Licensed member, AND no open period at
 * all. Covers Basic (user_type 1) and no-membership (user_type null) alike — both have nothing a period
 * endpoint could touch, and the guard pipeline already has a defined, audited outcome for the
 * no-membership sub-case (`skip_no_membership`) rather than needing a client-side guess. Every OTHER
 * combination (Licensed, or a Basic/no-membership row that still carries a live period) keeps the
 * unchanged PeriodModal path — see PoolUsersPage.tsx's own Manage-column cell.
 */
export function poolUserUnlinkOnly(user: PoolUser): boolean {
  return user.user_type !== 2 && user.period_id == null;
}

/**
 * rev 17b (N1, addendum) — a row the multi-select checkbox must refuse, "ship now not later": the
 * account owner/admin (`role_id` '0'/'1' — a bulk action on them is a GUARANTEED `skip_role_id`, and
 * page-scoped select-all would otherwise habitually sweep them in), or a row that already carries an
 * open period (`period_id != null` — the server would refuse it with `skip_has_open_period` anyway; its
 * real path is the per-row Manage button → PeriodModal's own force buttons, not the membership-keyed
 * bulk path). `mirror_ready === false` rows deliberately stay selectable — same "announce, don't block"
 * precedent as everywhere else this field appears; the confirm dialog surfaces the warning instead.
 */
export function poolUserSelectDisabled(user: PoolUser): boolean {
  return user.role_id === "0" || user.role_id === "1" || user.period_id != null;
}

/** `accountId` (rev 6 R5/R6) is the exact `?account=` filter a Pool Accounts row click seeds — combines
 *  with the free-text `term` rather than replacing it, so a search box refinement still narrows within
 *  the one account. `license` (rev 9, 21/7) composes the same way — "all" collapses to no filter both in
 *  the query key and on the wire, so an explicit "all" and an omitted filter share one cache entry. */
export function usePoolUsers(term: string, accountId?: string, license?: PoolUserLicenseFilter) {
  const debounced = useDebounced(term.trim(), DEBOUNCE_MS);
  const wireLicense = licenseWireValue(license);
  return useQuery({
    queryKey: poolKeys.users(debounced, accountId, wireLicense),
    queryFn: ({ signal }) => api.poolUsers(debounced, accountId, wireLicense, signal),
  });
}

export function usePoolCustomerHistory(email: string | null) {
  return useQuery({
    queryKey: poolKeys.history(email ?? ""),
    queryFn: ({ signal }) => api.poolCustomerHistory(email as string, signal),
    enabled: email !== null,
  });
}

export function usePoolOperators() {
  return useQuery({
    queryKey: poolKeys.operators,
    queryFn: ({ signal }) => api.poolOperators(signal),
  });
}

export function usePoolDashboard(enabled: boolean = true) {
  return useQuery({
    queryKey: poolKeys.dashboard,
    queryFn: ({ signal }) => api.poolDashboard(signal),
    // ปิด fetch ไปพร้อมกับการ์ด mismatch ที่ถูกซ่อน (SHOW_MISMATCH_CARDS ใน PoolDashboardPage) —
    // จอไม่แสดงก็ไม่ต้องโหลดข้อมูลพันกว่าแถวมาทิ้ง
    enabled,
  });
}

/** Powers FreshnessBadge on the Accounts + Dashboard screens. Refetched periodically so the badge
 *  keeps up with the 5-minute sweep cadence without a manual reload. */
export function usePoolStatus() {
  return useQuery({
    queryKey: poolKeys.status,
    queryFn: ({ signal }) => api.poolStatus(signal),
    refetchInterval: 60_000,
  });
}

/** Poll cadence for the force/bulk-force convergence wait (convergeSweepThenSettle, further below) —
 *  UNRELATED to "Sync now"'s own poll (POOL_SYNC_NOW_POLL_INTERVAL_MS below): that's a separate, more
 *  precise completion-signal state machine (rev 17c), this one just waits out a sweep round after an
 *  operator-forced write. Kept as its own constant rather than shared so the two cadences can diverge
 *  independently without one rev's tuning silently retuning the other. */
export const POOL_SWEEP_POLL_INTERVAL_MS = 5_000;

/** Bounded poll cadence after a "Sync now" enqueue — see useRunPoolSweep below. Exported so tests can
 *  reason about the exact interval/count instead of hard-coding a copy of these numbers. ~4s × 30 tries
 *  ≈ 2 minutes (rev 17c, 23/7 — "Sync now" feedback, option A: a targeted completion signal replaces the
 *  old blind re-invalidate-and-hope cadence, see this file's own useRunPoolSweep banner). */
export const POOL_SYNC_NOW_POLL_INTERVAL_MS = 4_000;
export const POOL_SYNC_NOW_POLL_MAX_TRIES = 30;

/** The states useRunPoolSweep's button-facing machine settles into. `posting`/`running` are transient
 *  (drive the button's spinner+label, never toast); every other value is terminal and toasts exactly
 *  once (see PoolAccountsPage's own SyncNowButton). `error` carries the raw thrown value (never
 *  pre-formatted here — hooks stay translation-agnostic, same convention as useSyncNow.ts) so the caller
 *  derives the verbatim server message via its own errMessage() helper. */
export type PoolSyncNowPhase =
  | { phase: "idle" }
  | { phase: "posting" }
  | { phase: "running" }
  | { phase: "complete" }
  | { phase: "paused" }
  | { phase: "timeout" }
  | { phase: "alreadyRunning" }
  | { phase: "error"; error: unknown };

/**
 * "Sync now" (Pool Accounts header, rev 8 — owner/admin only, see PoolAccountsPage; feedback flow
 * rewritten rev 17c 23/7). Enqueues an out-of-cadence sweep run, then — unless the enqueue itself landed
 * in the daily pause window — polls GET /pool/status for a NEW `last_complete.id` (a sweep round that
 * finished AFTER this click) instead of blindly re-invalidating on a fixed cadence and hoping: the
 * baseline id is captured BEFORE the POST fires (so a round that was already mid-flight can't be
 * mistaken for this run's own completion), and the poll only settles — and only THEN invalidates the
 * screens that read sweep output — once the id actually moves. Bounded to
 * POOL_SYNC_NOW_POLL_MAX_TRIES × POOL_SYNC_NOW_POLL_INTERVAL_MS (~2 minutes) so a sweep that stalls (or a
 * status endpoint that stays broken) can't poll forever; that bound settles as `timeout`, not `error` —
 * the enqueue itself DID succeed, there's just nothing more this screen can usefully wait for.
 *
 * `in_pause_window` (types.ts) short-circuits straight to `paused` — there is no run to poll for.
 */
export function useRunPoolSweep(): {
  phase: PoolSyncNowPhase;
  /** True whenever the button should show its spinner+disabled state — the POST, AND the poll that
   *  follows it, both count (see this hook's own banner). */
  isBusy: boolean;
  run: () => void;
} {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<PoolSyncNowPhase>({ phase: "idle" });
  // Cancels a still-running poll loop's future ticks — on unmount, and so a second click during an
  // active poll can't leave two loops racing to settle the same phase state. `timer` is the CURRENTLY
  // pending setTimeout handle for this token, kept so unmount can clearTimeout it outright rather than
  // rely solely on tick() noticing `cancelled` — belt and suspenders: without the explicit clear, an
  // already-scheduled tick still fires (as a no-op) instead of never firing at all.
  const cancelRef = useRef<{ cancelled: boolean; timer?: ReturnType<typeof setTimeout> } | null>(
    null,
  );
  // React Query calls a mutation's onSuccess even after the observing component has unmounted (it lives
  // on the Mutation itself, not the observer — see TanStack Query's mutation.execute()). Without this,
  // a click whose response lands after unmount would create a FRESH token here that the effect below
  // never gets a chance to cancel (its cleanup already ran), and that token's poll would run uncancelled
  // for up to POOL_SYNC_NOW_POLL_MAX_TRIES ticks, potentially firing setTimeout into a torn-down tree.
  const mountedRef = useRef(true);

  useEffect(() => {
    // SET IT, do not just clear it. `useRef(true)` alone is a ONE-WAY LATCH, and main.tsx wraps the app
    // in <StrictMode>, which in dev runs effect → cleanup → effect on the FIRST mount: the cleanup below
    // would flip this to false before the component was ever really mounted, and "Sync now" would give
    // the operator no feedback at all for the rest of the session. Testing Library's act environment
    // suppresses that double-invoke, so no test in this repo can see it — it only shows up in `bun run
    // dev` and in production's own StrictMode render. Assigning on every effect run is the standard
    // StrictMode-safe shape and costs nothing.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (cancelRef.current) {
        cancelRef.current.cancelled = true;
        if (cancelRef.current.timer !== undefined) clearTimeout(cancelRef.current.timer);
      }
    };
  }, []);

  function settleAfterComplete() {
    void qc.invalidateQueries({ queryKey: poolKeys.accounts });
    void qc.invalidateQueries({ queryKey: ["pool", "users"] });
    void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
    void qc.invalidateQueries({ queryKey: poolKeys.status });
  }

  function pollForCompletion(
    baseline: number | null,
    token: { cancelled: boolean; timer?: ReturnType<typeof setTimeout> },
  ) {
    let tries = 0;
    const tick = () => {
      void (async () => {
        if (token.cancelled) return;
        tries += 1;
        try {
          const st = await api.poolStatus();
          const currentId = st.last_complete?.id ?? null;
          if (token.cancelled) return;
          if (currentId != null && currentId !== baseline) {
            setPhase({ phase: "complete" });
            settleAfterComplete();
            return;
          }
        } catch {
          /* transient — keep polling within the same bound */
        }
        if (token.cancelled) return;
        if (tries >= POOL_SYNC_NOW_POLL_MAX_TRIES) {
          setPhase({ phase: "timeout" });
          return;
        }
        token.timer = setTimeout(tick, POOL_SYNC_NOW_POLL_INTERVAL_MS);
      })();
    };
    token.timer = setTimeout(tick, POOL_SYNC_NOW_POLL_INTERVAL_MS);
  }

  const mutation = useMutation({
    mutationFn: async () => {
      // Baseline captured BEFORE the enqueue fires — this hook's own banner explains why the ordering
      // matters. Best-effort: a failed baseline read still lets the enqueue proceed, it just makes the
      // eventual completion check less precise (ANY last_complete row settles it, see pollForCompletion).
      let baseline: number | null = null;
      try {
        baseline = (await api.poolStatus()).last_complete?.id ?? null;
      } catch {
        /* best-effort */
      }
      const res = await api.poolSweepRun();
      return { res, baseline };
    },
    onSuccess: ({ res, baseline }) => {
      // The enqueue itself already happened server-side (api.poolSweepRun above) regardless of mount
      // state — only THIS component's own polling/UI is skipped once it's gone, per the banner on
      // mountedRef above.
      if (!mountedRef.current) return;
      if (res.in_pause_window) {
        setPhase({ phase: "paused" });
        return;
      }
      setPhase({ phase: "running" });
      const token = { cancelled: false };
      cancelRef.current = token;
      pollForCompletion(baseline, token);
    },
    onError: (e) => {
      if (!mountedRef.current) return;
      if (e instanceof ApiRequestError && e.error.status === 409) {
        setPhase({ phase: "alreadyRunning" });
      } else {
        setPhase({ phase: "error", error: e });
      }
    },
  });

  function run() {
    if (cancelRef.current) {
      cancelRef.current.cancelled = true;
      if (cancelRef.current.timer !== undefined) clearTimeout(cancelRef.current.timer);
    }
    setPhase({ phase: "posting" });
    mutation.mutate();
  }

  return { phase, isBusy: mutation.isPending || phase.phase === "running", run };
}

/** The filter bar's committed values (PoolAuditPage) — `before_id`/`limit` are NOT here, they're owned
 *  by usePoolAuditFeed's own pagination, never passed in by the caller. */
export interface PoolAuditFilters {
  email?: string;
  actor?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
}

/** One page's row count — also the "did the server run out of rows" signal (see getNextPageParam
 *  below): the /audit envelope carries no next_cursor/has_more flag, so a page shorter than this IS
 *  end-of-results, the same convention Table's own client pagination uses. */
export const POOL_AUDIT_PAGE_SIZE = 50;

/**
 * Pool audit log (SaaS-standard tab) — server-side KEYSET pagination via `before_id` (the last row's
 * own id), not an opaque cursor string like /activity's. The feed is COMMIT-ordered (BE: ORDER BY
 * id DESC alone — id is NOT monotonic with ts across concurrent transactions, e.g. a human write
 * committing mid-sweep, so a (ts,id) sort over this single-id cursor would skip/duplicate rows;
 * each row displays its own ts), making "id < before_id" an exact "everything older" filter.
 * Re-fetches from page 1 whenever `filters` changes (a new queryKey) — same behaviour as AuditPage's
 * useInfiniteQuery over the portal-wide activity feed.
 */
export function usePoolAuditFeed(filters: PoolAuditFilters) {
  return useInfiniteQuery({
    queryKey: poolKeys.audit(filters),
    queryFn: ({ pageParam, signal }) =>
      api.poolAudit(
        { ...filters, ...(pageParam ? { before_id: pageParam } : {}), limit: POOL_AUDIT_PAGE_SIZE },
        signal,
      ),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) =>
      last.audit.length === POOL_AUDIT_PAGE_SIZE
        ? last.audit[last.audit.length - 1]?.id
        : undefined,
  });
}

/**
 * Reports tab list view (30/7) — the daily snapshot range. `range` is always FULLY resolved by the
 * caller (PoolReportsPage's own DateRangePicker default, never left to the server's implicit
 * end=today/start=end-29 fallback — see PoolReportsDailyParams's banner in types.ts) so the query key
 * changes exactly when the visible range does.
 */
export function usePoolReportsDaily(range: { start: string; end: string }) {
  return useQuery({
    queryKey: poolKeys.reportsDaily(range),
    queryFn: ({ signal }) => api.poolReportsDaily(range, signal),
  });
}

/** Reports tab day-drill-down (30/7) — `enabled` mirrors whether `?date=` is actually set (the caller
 *  passes `false` while on the list view, so no request fires until a row is clicked / a deep link
 *  lands with a date). A 404 (no snapshot for that date) surfaces as an ordinary ApiRequestError — the
 *  screen renders a dedicated "no snapshot" state for it. */
export function usePoolReportDay(date: string, enabled: boolean) {
  return useQuery({
    queryKey: poolKeys.reportDay(date),
    queryFn: ({ signal }) => api.poolReportsDay(date, signal),
    enabled,
  });
}

/** The open-intents queue (Phase 2) — no polling: an operator opens this screen when the tripwire/
 *  dashboard points them at it, not as an always-on ticker. */
export function usePoolIntents() {
  return useQuery({
    queryKey: poolKeys.intents,
    queryFn: ({ signal }) => api.poolIntents(signal),
  });
}

/**
 * Never 403s, so every consumer can rely on `data?.role` defaulting to `undefined`/falsy while pending,
 * without ever having to render <PoolErrorState/> for this call specifically. A generous staleTime is
 * deliberate: AppShell fires this once per session to decide nav visibility, and every tab/section gate
 * below reads the SAME cached result rather than re-fetching — it changes only when an Owner/Admin edits
 * the allow-list, not on a normal click cadence. `open_intents` rides along on the same payload so the
 * Intents tab's red badge (rev 6 R7) shares this one query too.
 */
export function usePoolAccess() {
  return useQuery({
    queryKey: poolKeys.access,
    queryFn: ({ signal }) => api.poolAccess(signal),
    staleTime: 5 * 60_000,
  });
}

/** `_manage` only — see PoolSystemPage. Refetches on mount/focus like any other query (deliberately no
 *  staleTime override: this is the one screen where "how armed is it RIGHT NOW" must never read stale). */
export function usePoolSystemState() {
  return useQuery({
    queryKey: poolKeys.systemState,
    queryFn: ({ signal }) => api.poolSystemState(signal),
  });
}

export function useSetPoolSystemState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PoolSetSystemStateBody) => api.poolSetSystemState(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: poolKeys.systemState }),
  });
}

// --- Mutations — each invalidates the query(s) it affects. -----------------------------------------

export function useAddPoolAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => api.poolAddAccount(accountId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pool", "candidates"] });
      void qc.invalidateQueries({ queryKey: poolKeys.accounts });
    },
  });
}

/** Add a pool account STANDALONE, off a live-verified Zoom credential (rev 6 R4) — no `candidates`
 *  invalidation: this account was never a ZPCD-mirrored candidate to begin with. */
export function useAddPoolAccountFromCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PoolAddCredentialBody) => api.poolAddAccountFromCredential(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: poolKeys.accounts }),
  });
}

export function useSetPoolAccountEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { accountId: string; enabled: boolean }) =>
      api.poolSetAccountEnabled(v.accountId, v.enabled),
    onSuccess: () => void qc.invalidateQueries({ queryKey: poolKeys.accounts }),
  });
}

/** rev 15 — re-verify a standalone account's stored credential on demand (the Test button). */
export function usePoolCredentialTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => api.poolCredentialTest(accountId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: poolKeys.accounts }),
  });
}

/** rev 15.2 — set/clear the operator-entered Zoom subscription PERIOD (standalone accounts).
 *  Also invalidates the dashboard: card 3 (two clocks) reads this value for standalone rows. */
export function usePoolSetSubscriptionPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      accountId: string;
      subscriptionStart: string | null;
      subscriptionEnd: string | null;
    }) => api.poolSetSubscriptionPeriod(v.accountId, v.subscriptionStart, v.subscriptionEnd),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poolKeys.accounts });
      void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
    },
  });
}

/** rev 16 — set/clear the operator-entered Account Type + Seats (standalone accounts). Also
 *  invalidates the dashboard: the Overview stats bucket by account type and sum seats, both of
 *  which read this value for standalone rows. */
export function usePoolSetManualAccountInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { accountId: string; accountType: string | null; seats: number | null }) =>
      api.poolSetManualAccountInfo(v.accountId, v.accountType, v.seats),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poolKeys.accounts });
      void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
    },
  });
}

/** Removes an account from the pool (rev 7 — RemovePoolAccountDialog). Invalidates every surface that
 *  reads a pool account: the accounts list itself, Pool Users (a removed account's rows disappear), and
 *  the dashboard mismatch cards (a card 1/2 row keyed on this account is now moot). */
export function useRemovePoolAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => api.poolRemoveAccount(accountId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poolKeys.accounts });
      void qc.invalidateQueries({ queryKey: ["pool", "users"] });
      void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
    },
  });
}

export function useAddPoolOperator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PoolAddOperatorBody) => api.poolAddOperator(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poolKeys.operators });
      // The picker's `is_pool_operator` flag for this email is now stale (see PoolUserCandidate).
      void qc.invalidateQueries({ queryKey: ["pool", "userCandidates"] });
    },
  });
}

export function useRemovePoolOperator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.poolRemoveOperator(email),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poolKeys.operators });
      void qc.invalidateQueries({ queryKey: ["pool", "userCandidates"] });
    },
  });
}

function invalidateAfterPeriodChange(qc: ReturnType<typeof useQueryClient>, email: string) {
  void qc.invalidateQueries({ queryKey: ["pool", "users"] });
  void qc.invalidateQueries({ queryKey: poolKeys.history(email) });
  void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
}

export function useCreatePeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PoolCreatePeriodBody) => api.poolCreatePeriod(body),
    onSuccess: (_res, vars) => invalidateAfterPeriodChange(qc, vars.email),
  });
}

export function useUpdatePeriod(email: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { periodId: number; body: PoolUpdatePeriodBody }) =>
      api.poolUpdatePeriod(v.periodId, v.body),
    onSuccess: () => invalidateAfterPeriodChange(qc, email),
  });
}

export function useRenewDecision(email: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { periodId: number; body: PoolRenewDecisionBody }) =>
      api.poolRenewDecision(v.periodId, v.body),
    onSuccess: () => invalidateAfterPeriodChange(qc, email),
  });
}

export function useRenewPeriod(email: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { periodId: number; body: PoolRenewBody }) => api.poolRenew(v.periodId, v.body),
    onSuccess: () => invalidateAfterPeriodChange(qc, email),
  });
}

export function useClosePeriod(email: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { periodId: number; body: PoolCloseBody }) =>
      api.poolClosePeriod(v.periodId, v.body.reason),
    onSuccess: () => invalidateAfterPeriodChange(qc, email),
  });
}

/** Resolving an intent both clears it from the queue AND may change what the sweep sees next time
 *  (a resolved_failed retries), so it invalidates the same surfaces a normal period mutation does, PLUS
 *  the intents queue itself AND `access` — the Intents tab's red count (rev 6 R7) rides on that payload,
 *  so a resolve that empties the queue must make the badge (and the tab itself) disappear promptly. */
export function useResolveIntent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { auditId: number; body: PoolResolveIntentBody }) =>
      api.poolResolveIntent(v.auditId, v.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: poolKeys.intents });
      void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
      void qc.invalidateQueries({ queryKey: ["pool", "users"] });
      void qc.invalidateQueries({ queryKey: poolKeys.access });
    },
  });
}

/** Poll window for the post-force convergence below — wider than the Sync-now poll because it
 *  waits for a WHOLE sweep round to complete, not just the caches to refresh. */
export const POOL_FORCE_CONVERGE_MAX_TRIES = 12;

/**
 * After a REAL (non-dry) successful force, the row's License pill must move NOW — the operator
 * just watched the server confirm the Zoom write (`ok_*`), and a pill that sits on "Licensed"
 * until the next 5-minute sweep tick reads as "nothing happened" (คุณใหม่, 22/7 — first live
 * force). Truth-preserving convergence:
 *   1) optimistic setQueriesData over EVERY cached pool-users variant — Licensed→Basic for a
 *      license-remove, membership-gone for an unlink. Not a guess: the server's own result said
 *      the Zoom write succeeded;
 *   2) best-effort out-of-cadence sweep enqueue (a 409 "already running" is fine — swallowed);
 *   3) poll GET /pool/status until a sweep round completes AFTER the force (last_complete id
 *      moves past the baseline), and ONLY THEN invalidate the users caches — invalidating any
 *      earlier would refetch the pre-sweep membership and flap the pill Licensed→Basic→Licensed
 *      →Basic. One flip, then observed truth confirms it.
 * Dry-run and guard-abort results change NOTHING here: no ok_* counter → no optimistic write,
 * and the caller falls back to the ordinary immediate invalidation.
 * Returns true when it took ownership of the users-cache convergence.
 *
 * (rev 17b: the wait/settle mechanics — one baseline read, one best-effort sweep enqueue, one
 * bounded poll loop, THEN settle — are extracted below as `convergeSweepThenSettle` so
 * `convergeUsersAfterBulkForce` reuses the EXACT same wait behaviour instead of a hand-copied,
 * inevitably-diverging second version. The query client it takes is the loop's OWNER — wave-7 C2,
 * see convergeLifecycle.ts — never a cache writer: only the caller-supplied `settle` closure ever
 * touches the cache.)
 */
function convergeSweepThenSettle(qc: QueryClient, settleCb: () => void): void {
  // C2 (wave 7): the loop's lifecycle is owned by the QUERY CLIENT (convergeLifecycle.ts), not by the
  // dialog/screen that started it — a dialog closing must not cut it off, but a cleared cache or an
  // unmounted provider cancels it (timers cleared, no settle), and a second force supersedes the
  // first loop instead of running a parallel one.
  startConvergeLoop(qc, settleCb, (signal, settle) => {
    let timer: number | undefined;
    signal.addEventListener("abort", () => window.clearTimeout(timer), { once: true });
    const schedule = () => {
      if (signal.aborted) return;
      timer = window.setTimeout(() => void poll(), POOL_SWEEP_POLL_INTERVAL_MS);
    };
    let baseline: number | null = null;
    let tries = 0;
    // Cancellation needs no check inside poll(): an abort clears the pending timer, `settle` is a no-op
    // once the signal has aborted (a status answer that lands after a clear/unmount fires nothing), and
    // `schedule` refuses to arm a new timer — so a cancelled loop dies at its next step whatever it was.
    const poll = async () => {
      tries += 1;
      try {
        const st = await api.poolStatus();
        if ((st.last_complete?.id ?? null) !== baseline) {
          settle();
          return;
        }
      } catch {
        /* transient — keep polling */
      }
      if (tries < POOL_FORCE_CONVERGE_MAX_TRIES) {
        schedule();
      } else {
        settle(); // give up waiting — let the ordinary cadence surface the truth
      }
    };
    void (async () => {
      try {
        baseline = (await api.poolStatus()).last_complete?.id ?? null;
      } catch {
        /* status unreadable → still enqueue; the bounded poll below settles regardless */
      }
      if (signal.aborted) return; // cancelled before the baseline landed — don't enqueue for a dead cache
      void api.poolSweepRun().catch(() => undefined);
      schedule();
    })();
  });
}

/** The period columns a successful force/bulk-force closes server-side (DB truth, immediate) — cleared
 *  in the SAME optimistic write as the license/membership flip so the row lands in its complete
 *  post-action state in one paint, never a flash of a half-updated row. */
const CLOSED_PERIOD_PATCH = {
  period_id: null,
  period_start: null,
  period_end: null,
  renew: null,
  expiry_action: null,
  grace_days: null,
  grace_started_on: null,
} satisfies Partial<PoolUser>;

function convergeUsersAfterForce(
  qc: ReturnType<typeof useQueryClient>,
  email: string,
  res: PoolForceActionResult,
  kind: "license-remove" | "unlink",
): boolean {
  const wroteForReal =
    !res.dry_run &&
    ((kind === "license-remove" && (res.actions["ok_license_only"] ?? 0) > 0) ||
      (kind === "unlink" && (res.actions["ok_unlink"] ?? 0) > 0));
  if (!wroteForReal) return false;
  qc.setQueriesData<PoolUsers>({ queryKey: ["pool", "users"] }, (page) =>
    page
      ? {
          ...page,
          users: page.users.map((u) =>
            u.email !== email
              ? u
              : kind === "license-remove"
                ? { ...u, ...CLOSED_PERIOD_PATCH, user_type: 1 }
                : {
                    ...u,
                    ...CLOSED_PERIOD_PATCH,
                    user_type: null,
                    user_status: null,
                    role_id: null,
                  },
          ),
        }
      : page,
  );
  convergeSweepThenSettle(qc, () => {
    void qc.invalidateQueries({ queryKey: ["pool", "users"] });
    void qc.invalidateQueries({ queryKey: poolKeys.accounts });
    void qc.invalidateQueries({ queryKey: poolKeys.status });
  });
  return true;
}

/**
 * rev 17b — the bulk/UnlinkOnlyDialog sibling of convergeUsersAfterForce above, generalized to N
 * emails from ONE `bulk-force` call's flat `results` map (PoolBulkForceResult) WITHOUT firing N poll
 * loops: every email that actually wrote gets the SAME single optimistic pass + the SAME single
 * baseline/enqueue/poll cycle, converging together on the same sweep round (rev17b-addendum.md F6 /
 * rev17b-fe-design.md §3). Returns true when it took ownership of the users-cache convergence (the
 * caller falls back to a blanket invalidate otherwise — same rule as the single-force hooks).
 */
export function convergeUsersAfterBulkForce(
  qc: ReturnType<typeof useQueryClient>,
  results: Record<string, string>,
  dryRun: boolean,
  action: PoolBulkForceAction,
): boolean {
  if (dryRun) return false;
  const okCode = action === "license_remove" ? "ok_license_only" : "ok_unlink";
  const wroteFor = Object.entries(results)
    .filter(([, code]) => code === okCode)
    .map(([email]) => email);
  if (wroteFor.length === 0) return false;
  const wroteSet = new Set(wroteFor);
  qc.setQueriesData<PoolUsers>({ queryKey: ["pool", "users"] }, (page) =>
    page
      ? {
          ...page,
          users: page.users.map((u) =>
            !wroteSet.has(u.email)
              ? u
              : action === "license_remove"
                ? { ...u, ...CLOSED_PERIOD_PATCH, user_type: 1 }
                : {
                    ...u,
                    ...CLOSED_PERIOD_PATCH,
                    user_type: null,
                    user_status: null,
                    role_id: null,
                  },
          ),
        }
      : page,
  );
  convergeSweepThenSettle(qc, () => {
    void qc.invalidateQueries({ queryKey: ["pool", "users"] });
    void qc.invalidateQueries({ queryKey: poolKeys.accounts });
    void qc.invalidateQueries({ queryKey: poolKeys.status });
  });
  return true;
}

/** The chunk size the FE splits a selection into before calling bulk-force — mirrors the server's
 *  `pool_expiry_blast_cap` DEFAULT (10; rev17b-addendum.md N2 — a loud comment on this same coupling
 *  lives on the API side, router.py). A page-scoped selection (Table's own page-size ceiling, up to
 *  100) can exceed the cap, so a bulk confirm sends its emails as sequential chunks of AT MOST this
 *  many, aggregating results into one panel rather than one request per email or one giant request the
 *  server would 422 for being oversized. */
export const POOL_BULK_CHUNK_SIZE = 10;

/** Thin mutation wrapper — BulkForceDialog owns the chunking LOOP itself (sequential, progress-tracked,
 *  stops on a request-level abort) and calls `mutateAsync` once per chunk, so this hook deliberately
 *  carries no onSuccess side effect of its own; convergence happens ONCE for the whole run via
 *  `convergeUsersAfterBulkForce` above, not once per chunk. */
export function useBulkForce() {
  return useMutation({
    mutationFn: (v: { action: PoolBulkForceAction; emails: string[] }) =>
      api.poolBulkForce(v.action, v.emails),
  });
}

/** Thin mutation wrapper for the bulk Set-period run loop — mirrors `useBulkForce` in carrying NO
 *  per-call onSuccess: BulkSetPeriodDialog owns the sequential one-POST-per-email loop and converges the
 *  caches ONCE in its own finish(), so an onSuccess here would fire N redundant invalidations. Unlike
 *  useCreatePeriod (the single-create hook, which DOES invalidate per call), this is the batch primitive. */
export function useBulkSetPeriod() {
  return useMutation({
    mutationFn: (body: PoolCreatePeriodBody) => api.poolCreatePeriod(body),
  });
}

export function useForceLicenseRemove(email: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (periodId: number) => api.poolForceLicenseRemove(periodId),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: poolKeys.history(email) });
      void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
      void qc.invalidateQueries({ queryKey: poolKeys.intents });
      if (!convergeUsersAfterForce(qc, email, res, "license-remove")) {
        void qc.invalidateQueries({ queryKey: ["pool", "users"] });
      }
    },
  });
}

export function useForceUnlink(email: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (periodId: number) => api.poolForceUnlink(periodId),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: poolKeys.history(email) });
      void qc.invalidateQueries({ queryKey: poolKeys.dashboard });
      void qc.invalidateQueries({ queryKey: poolKeys.intents });
      if (!convergeUsersAfterForce(qc, email, res, "unlink")) {
        void qc.invalidateQueries({ queryKey: ["pool", "users"] });
      }
    },
  });
}
